import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardIos';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'core/hooks/hooks';
import { listExpenses } from 'core/store/actions/expenses.actions';
import { IPeriodState, setPeriod } from 'core/store/reducers/period.reducer';
import { periodSelector } from 'core/store/selectors/period.selector';
import { useCallback, useMemo } from 'react';
import { getMonthItem, isCurrentMonth } from 'utils/dateUtils';

export interface MonthSelectorProps {
  target?: keyof IPeriodState;
  initialMonth?: Date;
  fullWidth?: boolean;
}

const today = new Date();

const MonthSelectorPrevNextButton = ({
  onClick,
  direction
}: {
  direction: 'prev' | 'next';
  onClick: () => void;
}) => {
  return (
    <IconButton
      size="small"
      sx={{
        mr: direction === 'prev' ? '10px' : 0,
        ml: direction === 'next' ? '10px' : 0
      }}
      onClick={onClick}
    >
      {direction === 'prev' ? <ArrowBackIcon /> : <ArrowForwardIcon />}
    </IconButton>
  );
};

export const MonthSelector = ({
  target = 'default',
  fullWidth
}: MonthSelectorProps) => {
  const selectedMonth = useAppSelector(periodSelector);
  const monthDate = new Date(selectedMonth.year, selectedMonth.month);

  const dispatch = useAppDispatch();
  const handlePrevMonth = useCallback(() => {
    const prevMonth = new Date(selectedMonth.year, selectedMonth.month);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    updateMonth(prevMonth);
  }, [selectedMonth]);

  const handleNextMonth = useCallback(() => {
    const nextMonth = new Date(selectedMonth.year, selectedMonth.month);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    updateMonth(nextMonth);
  }, [selectedMonth]);

  const updateMonth = (month: Date) => {
    const monthItem = getMonthItem(month);
    dispatch(setPeriod({ target, month: monthItem }));
    dispatch(listExpenses(monthItem));
  };

  const handleResetBudgetMonth = () => updateMonth(today);

  const currentMonth = useMemo(
    () => isCurrentMonth(selectedMonth),
    [selectedMonth]
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <MonthSelectorPrevNextButton direction="prev" onClick={handlePrevMonth} />
      <Box sx={{ flexGrow: fullWidth ? 1 : undefined }}>
        <Typography sx={{ textAlign: 'center', fontWeight: 'bold' }}>
          {monthDate.toLocaleDateString('en-US', {
            month: 'short',
            year: 'numeric'
          })}
        </Typography>
      </Box>
      <MonthSelectorPrevNextButton direction="next" onClick={handleNextMonth} />
      {!currentMonth && (
        <Button
          variant="outlined"
          size="small"
          sx={{ marginLeft: '10px' }}
          onClick={handleResetBudgetMonth}
        >
          reset
        </Button>
      )}
    </Box>
  );
};
